import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { useToast } from '../ui/use-toast';
import { type User } from '../../services/staff';
import { scheduleEventsService, ScheduleEventError } from '../../services/schedule-events';
import { ScheduleUserSearch } from './ScheduleUserSearch';

interface ScheduleAddUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  gymSlug: string;
  eventId: string | null;
  occurrenceDate: string | null;
  eventTitle: string;
  dayLabel: string;
  onDone: () => void;
}

/** Staff (admin+editor): add a single user to one occurrence. This is the entry
 * point for enrolling people into staff-only events. */
export function ScheduleAddUserDialog({
  open,
  onOpenChange,
  gymSlug,
  eventId,
  occurrenceDate,
  eventTitle,
  dayLabel,
  onDone,
}: ScheduleAddUserDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [selected, setSelected] = useState<User | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setSelected(null);
  }, [open]);

  const handleSubmit = async () => {
    if (!eventId || !occurrenceDate || !selected) return;
    setSaving(true);
    try {
      await scheduleEventsService.staffRegister(gymSlug, eventId, {
        user_slug: selected.slug,
        occurrence_date: occurrenceDate,
      });
      toast({
        title: t('schedule.addUser.success', { name: selected.full_name || selected.username }),
      });
      onDone();
      onOpenChange(false);
    } catch (err) {
      const message =
        err instanceof ScheduleEventError && err.code === 'already_registered'
          ? t('schedule.addUser.alreadyRegistered')
          : err instanceof Error
            ? err.message
            : t('errors.unexpectedError');
      toast({ title: message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('schedule.addUser.title', { title: eventTitle })}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">{dayLabel}</p>
          <div className="space-y-1.5">
            <Label>{t('schedule.bulkRegister.userLabel')}</Label>
            <ScheduleUserSearch selected={selected} onSelect={setSelected} />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={saving || !selected}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t('schedule.addUser.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
